import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Mail, Check, Loader2, ArrowLeft, ArrowRight } from 'lucide-react';
import { Reveal } from './Reveal';
import { BRAND, FORM_OPTIONS, scrollToId } from '../config/site';
import { trackKindstoneLead } from './MetaPixelConsent';

const NETLIFY_FORM_NAME = 'kindstone-quote';

const EMPTY = {
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  zip_code: '',
  property_type: '',
  bedrooms: '',
  bathrooms: '',
  cleaning_service: '',
  square_footage: '',
  frequency: '',
  preferred_date: '',
  best_time: '',
  message: '',
};

const STEPS = [
  { title: 'Cleaning Details', fields: ['zip_code', 'property_type', 'bedrooms', 'bathrooms', 'cleaning_service'] },
  { title: 'Contact Details', fields: ['first_name', 'phone', 'email'] },
];

const REQUIRED_MSG = {
  first_name: 'First name is required',
  phone: 'Phone is required',
  email: 'Email is required',
  zip_code: 'ZIP code is required',
  property_type: 'Select a property type',
  bedrooms: 'Select bedrooms',
  bathrooms: 'Select bathrooms',
  cleaning_service: 'Select a cleaning service',
};

const INPUT_CLASS =
  'h-12 w-full rounded-xl border border-navy/15 bg-white px-4 text-base font-medium text-navy placeholder:font-normal placeholder:text-charcoal/40 transition-colors focus:border-electric';

const Label = ({ htmlFor, children, optional }) => (
  <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-bold text-navy">
    {children}
    {optional && <span className="ml-1 font-medium text-charcoal/50">(optional)</span>}
  </label>
);

const Field = ({ id, label, optional, error, testid, ...props }) => (
  <div>
    <Label htmlFor={id} optional={optional}>
      {label}
    </Label>
    <input id={id} data-testid={testid} className={INPUT_CLASS} {...props} />
    {error && <p className="mt-1 text-xs font-semibold text-red-600">{error}</p>}
  </div>
);

const Select = ({ id, label, value, onChange, options, error, testid, optional, placeholder = 'Select…', helperText }) => (
  <div>
    <Label htmlFor={id} optional={optional}>
      {label}
    </Label>
    <select
      id={id}
      data-testid={testid}
      value={value}
      onChange={onChange}
      className={`${INPUT_CLASS} appearance-none ${value ? '' : 'text-charcoal/40'}`}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%230B2D6B' stroke-width='2.4'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 1rem center',
      }}
    >
      <option value="" disabled>
        {placeholder}
      </option>
      {options.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
    {helperText && <p className="mt-1 text-xs leading-relaxed text-charcoal/60">{helperText}</p>}
    {error && <p className="mt-1 text-xs font-semibold text-red-600">{error}</p>}
  </div>
);

const ProgressBar = ({ step }) => (
  <div className="mb-8" data-testid="quote-form-progress">
    <div className="flex items-center justify-between">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-electric">
        Step {step + 1} of {STEPS.length}
        <span className="ml-2 normal-case tracking-normal text-charcoal/60">
          {STEPS[step].title}
        </span>
      </p>
      <p className="text-xs font-bold text-navy/50">{step + 1} / {STEPS.length}</p>
    </div>
    <div className="mt-3 flex gap-2">
      {STEPS.map((_, i) => (
        <span
          key={i}
          className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${
            i <= step ? 'bg-electric' : 'bg-navy/10'
          }`}
        />
      ))}
    </div>
  </div>
);

export const QuoteForm = () => {
  const [step, setStep] = useState(0);
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const hasMounted = useRef(false);

  // Reposition only on step or submission success, not while typing.
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    const frame = requestAnimationFrame(() => scrollToId('quote'));
    return () => cancelAnimationFrame(frame);
  }, [step, submitted]);

  const bind = (key) => ({
    value: f[key],
    onChange: (e) => setF({ ...f, [key]: e.target.value }),
  });

  const validateFields = (fields) => {
    const e = {};
    fields.forEach((k) => {
      if (REQUIRED_MSG[k] && !f[k].trim()) e[k] = REQUIRED_MSG[k];
    });
    if (fields.includes('email') && f.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email))
      e.email = 'Enter a valid email';
    if (fields.includes('phone') && f.phone && f.phone.replace(/\D/g, '').length < 7)
      e.phone = 'Enter a valid phone number';
    return e;
  };

  const nextStep = () => {
    const e = validateFields(STEPS[step].fields);
    setErrors(e);
    if (Object.keys(e).length) return;
    setStep((s) => s + 1);
  };

  const backStep = () => {
    setErrors({});
    setStep((s) => s - 1);
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setServerError('');
    const allFields = STEPS.flatMap((s) => s.fields);
    const e = validateFields(allFields);
    setErrors(e);
    if (Object.keys(e).length) return;
    setLoading(true);
    try {
      const payload = new URLSearchParams({
        'form-name': NETLIFY_FORM_NAME,
        'bot-field': '',
        ...f,
      });
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      if (!res.ok) throw new Error('Request failed');
      setSubmitted(true);
      // Conversion counts only after Netlify confirms a successful submission.
      // No contact, address, or form field data is sent to Meta.
      trackKindstoneLead();
    } catch {
      setServerError(
        "We couldn't submit your request right now. Please try again, or text or email us — we're happy to take your details that way."
      );
    } finally {
      setLoading(false);
    }
  };

  const stepAnim = {
    initial: { opacity: 0, x: 24 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -24 },
    transition: { duration: 0.22, ease: 'easeOut' },
  };

  return (
   <section id="quote-section" className="ks-quote relative scroll-mt-24 overflow-hidden pb-20 pt-28 lg:pb-28 lg:pt-36">
      <svg
        viewBox="0 0 1440 70"
        preserveAspectRatio="none"
        className="absolute inset-x-0 top-0 h-[46px] w-full"
        aria-hidden="true"
      >
        <path d="M0,0 L1440,0 L1440,10 C1100,58 420,60 0,16 Z" fill="#ffffff" />
      </svg>
      <div
        className="pointer-events-none absolute -left-40 bottom-0 h-[420px] w-[420px] rounded-full bg-aqua/10 blur-3xl"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -right-40 top-10 h-[420px] w-[420px] rounded-full bg-electric/10 blur-3xl"
        aria-hidden="true"
      />

      <div className="relative mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-[1fr_1.25fr] lg:gap-20 lg:px-8">
        <Reveal className="order-2 lg:order-1">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-aqua" data-testid="quote-eyebrow">
            Free Quote
          </p>
          <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Get Your Free Quote
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-white/80 sm:text-lg">
            Tell us about your home. We'll contact you personally to discuss your cleaning needs.
          </p>

          <div className="mt-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-aqua">What happens next</p>
            <ol className="mt-5 space-y-5">
              {[
                'We review your request.',
                'We contact you to understand your home and cleaning needs.',
                'We confirm the details together.',
              ].map((t, i) => (
                <li key={t} className="flex items-start gap-4">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-aqua text-sm font-extrabold text-navy">
                    {i + 1}
                  </span>
                  <p className="pt-1 text-base font-medium leading-relaxed text-white/85">{t}</p>
                </li>
              ))}
            </ol>
          </div>

          <div className="mt-10 border-t border-white/15 pt-8">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-aqua">
              Questions before requesting a quote?
            </p>
            <p className="mt-2 text-sm leading-relaxed text-white/70">
              Send us a message — we're happy to help.
            </p>
            <div className="mt-5 flex flex-col gap-3">
              <a
                href={BRAND.smsHref}
                data-testid="quote-side-text-button"
                className="inline-flex items-center gap-3 text-lg font-extrabold text-white transition-colors hover:text-aqua"
              >
                <MessageCircle size={20} aria-hidden="true" />
                <span>
                  <span className="block text-xs font-bold uppercase tracking-[0.18em] text-aqua">Text Us</span>
                  {BRAND.smsDisplay}
                </span>
              </a>
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="quote-side-email-button"
                className="inline-flex items-center gap-3 text-base font-semibold text-white/90 transition-colors hover:text-aqua"
              >
                <Mail size={18} className="shrink-0" aria-hidden="true" />
                <span>
                  <span className="block text-xs font-bold uppercase tracking-[0.18em] text-aqua">Email Us</span>
                  <span className="break-all">{BRAND.email}</span>
                </span>
              </a>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12} className="order-1 lg:order-2">
          <div
           id="quote"
            className="scroll-mt-24 rounded-[2rem] bg-white p-6 shadow-lift sm:p-10"
            data-testid="quote-form-card"
          >
            {!submitted && <h3 className="mb-5 text-xl font-extrabold text-navy lg:hidden">Get Your Free Quote</h3>}
            {submitted ? (
              <div
                className="flex min-h-[360px] flex-col items-center justify-center text-center"
                data-testid="quote-form-success-message"
              >
                <span className="grid h-20 w-20 place-items-center rounded-full bg-green-tint text-fresh">
                  <Check size={40} strokeWidth={3} />
                </span>
                <h3 className="mt-7 text-3xl font-extrabold text-navy">Thank You! Your Request Has Been Sent.</h3>
                <p className="mt-3 max-w-sm text-sm leading-relaxed text-charcoal sm:text-base">We've received your cleaning quote request. Our team will review your details and contact you soon.</p>

                <button
                  type="button"
                  data-testid="success-back-to-home-button"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="mt-8 rounded-full bg-electric px-8 py-3.5 text-sm font-bold uppercase tracking-wide text-white transition-all hover:-translate-y-0.5 hover:bg-navy"
                >
                  Back to Home
                </button>
              </div>
            ) : (
              <form
                name={NETLIFY_FORM_NAME}
                method="POST"
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                onSubmit={onSubmit}
                noValidate
              >
                <input type="hidden" name="form-name" value={NETLIFY_FORM_NAME} />
                <input type="hidden" name="bot-field" value="" />
                <ProgressBar step={step} />

                <AnimatePresence mode="wait">
                  {step === 0 && (
                    <motion.div key="step-0" {...stepAnim} className="grid gap-5 sm:grid-cols-2">
                      <Select id="cleaning_service" label="Cleaning Service" testid="quote-form-service-type"
                        {...bind('cleaning_service')} options={FORM_OPTIONS.cleaningService} error={errors.cleaning_service} />
                      <Select id="property_type" label="Property Type" testid="quote-form-property-type"
                        {...bind('property_type')} options={FORM_OPTIONS.propertyType} error={errors.property_type} />
                      <Select id="bedrooms" label="Bedrooms" testid="quote-form-bedrooms"
                        {...bind('bedrooms')} options={FORM_OPTIONS.bedrooms} error={errors.bedrooms} />
                      <Select id="bathrooms" label="Bathrooms" testid="quote-form-bathrooms"
                        {...bind('bathrooms')} options={FORM_OPTIONS.bathrooms} error={errors.bathrooms} />
                      <Select id="square-footage" label="Approximate Property Size (Sq Ft)" optional
                        testid="quote-form-sqft" {...bind('square_footage')}
                        options={["Up to 500 sq ft","501–1,000 sq ft","1,001–1,500 sq ft","1,501–2,000 sq ft","2,001–2,500 sq ft","2,501–3,000 sq ft","Over 3,000 sq ft","I'm not sure"]}
                        placeholder="Select approximate size"
                        helperText="Just an estimate is fine! You can skip this question." />
                      <Field id="zip" label="ZIP Code" inputMode="numeric" placeholder="34786"
                        testid="quote-form-zip" {...bind('zip_code')} error={errors.zip_code}
                        autoComplete="postal-code" />
                      <Select id="frequency" label="Cleaning Frequency" optional testid="quote-form-frequency"
                        {...bind('frequency')} options={FORM_OPTIONS.frequency} />
                      <Field id="preferred-date" label="Preferred Cleaning Date" optional type="date"
                        testid="quote-form-date" {...bind('preferred_date')} />
                    </motion.div>
                  )}

                  {step === 1 && (
                    <motion.div key="step-1" {...stepAnim} className="grid gap-5 sm:grid-cols-2">
                      <Field id="first-name" label="First Name" testid="quote-form-first-name"
                        {...bind('first_name')} error={errors.first_name} autoComplete="given-name" />
                      <Field id="last-name" label="Last Name" optional testid="quote-form-last-name"
                        {...bind('last_name')} autoComplete="family-name" />
                      <Field id="phone" label="Phone" type="tel" placeholder="(555) 555-5555"
                        testid="quote-form-phone" {...bind('phone')} error={errors.phone} autoComplete="tel" />
                      <Field id="email" label="Email" type="email" placeholder="you@email.com"
                        testid="quote-form-email" {...bind('email')} error={errors.email} autoComplete="email" />
                      <div className="sm:col-span-2">
                        <Select id="best-time" label="Best Time to Contact" optional testid="quote-form-best-time"
                          {...bind('best_time')} options={FORM_OPTIONS.bestTime} />
                      </div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="message" optional>Additional Details</Label>
                        <textarea
                          id="message" data-testid="quote-form-message" rows={4}
                          value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })}
                          placeholder="Anything else we should know about your cleaning needs?"
                          className="w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-base font-medium text-navy placeholder:font-normal placeholder:text-charcoal/40 transition-colors focus:border-electric"
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {serverError && (
                  <p
                    role="alert"
                    data-testid="quote-form-error-message"
                    className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
                  >
                    {serverError}
                  </p>
                )}

                <div className="mt-7 flex flex-col gap-3 sm:flex-row-reverse sm:items-center">
                  {step < STEPS.length - 1 ? (
                    <button
                      type="button"
                      data-testid="quote-form-next-button"
                      onClick={nextStep}
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-electric px-8 py-4 text-sm font-bold uppercase tracking-wide text-white shadow-soft transition-all hover:-translate-y-0.5 hover:bg-navy sm:flex-none"
                    >
                      Next: Contact Details
                      <ArrowRight size={16} />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="quote-form-submit-button"
                      className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-electric px-8 py-4 text-sm font-bold uppercase tracking-wide text-white shadow-soft transition-all hover:-translate-y-0.5 hover:bg-navy disabled:cursor-not-allowed disabled:opacity-70 sm:flex-none"
                    >
                      {loading && <Loader2 size={18} className="animate-spin" aria-hidden="true" />}
                      {loading ? 'Sending…' : 'Request My Free Quote'}
                    </button>
                  )}
                  {step > 0 && (
                    <button
                      type="button"
                      data-testid="quote-form-back-button"
                      onClick={backStep}
                      className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-navy/15 px-6 py-3.5 text-sm font-bold text-navy transition-colors hover:border-electric hover:text-electric"
                    >
                      <ArrowLeft size={15} />
                      Back
                    </button>
                  )}
                </div>

                {step === STEPS.length - 1 && (
                  <p className="mt-4 text-center text-xs leading-relaxed text-charcoal/70">
                    By submitting, you agree that KINDSTONE Cleaning Services may contact you by
                    text message or email about your quote request. We never share your information.
                  </p>
                )}
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default QuoteForm;
